import React, { useState } from 'react';
import { motion } from 'framer-motion';

export interface HouseRulesSettings {
  // Basic validation rules
  allowProperNouns: boolean;
  allowAlternateSpellings: boolean;
  allowMultiWordAnswers: boolean;
  allowForeignWords: boolean;

  // Length requirements
  minimumWordLength: number;

  // Scoring preferences
  scoringMode: 'simple' | 'enhanced';
  uniqueAnswerBonus: boolean;

  // Difficulty adjustments
  kidFriendlyMode: boolean;
  strictValidation: boolean;

  // Host powers
  hostCanOverride: boolean;

  /**
   * What happens when the first player hits Done.
   *   squeeze10 — everyone else gets `squeezeSeconds`. The paper rule.
   *   instant   — the round ends immediately. Hardcore.
   *   timerOnly — finishing early does nothing; the clock is the only authority.
   * Mirrors DEFAULT_HOUSE_RULES in backend/src/gameLogic.js.
   */
  finishRule?: 'squeeze10' | 'instant' | 'timerOnly';
  /**
   * How long everyone else gets once someone finishes. Was hardcoded at 10;
   * the host can set it now. The server clamps to `min(endsAt, now + n)`, so
   * this can only ever SHORTEN the clock — never extend it.
   */
  squeezeSeconds?: number;
  /**
   * Wrong answers cost you 5 points instead of scoring 0. Off by default —
   * it's a real punishment and most tables don't want it.
   */
  strictScoring?: boolean;
}

export const defaultHouseRules: HouseRulesSettings = {
  allowProperNouns: true,
  allowAlternateSpellings: true,
  allowMultiWordAnswers: true,
  allowForeignWords: false,
  minimumWordLength: 2,
  scoringMode: 'simple',
  uniqueAnswerBonus: false,
  kidFriendlyMode: false,
  strictValidation: false,
  hostCanOverride: true,
  finishRule: 'squeeze10',
  squeezeSeconds: 10,
  strictScoring: false
};

interface HouseRulesProps {
  settings: HouseRulesSettings;
  onChange: (settings: HouseRulesSettings) => void;
  isHost: boolean;
}

const HouseRules: React.FC<HouseRulesProps> = ({ settings, onChange, isHost }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!isHost) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium mb-2">🏠 House Rules Active</p>
        <ul className="space-y-1">
          {settings.allowProperNouns && <li>✓ Proper nouns allowed (names, places)</li>}
          {settings.allowAlternateSpellings && <li>✓ Alternate spellings accepted</li>}
          {settings.allowMultiWordAnswers && <li>✓ Multi-word answers OK (New York)</li>}
          {settings.minimumWordLength !== 2 && <li>✓ Minimum {settings.minimumWordLength} letters</li>}
          {settings.kidFriendlyMode && <li>✓ Kid-friendly mode active</li>}
        </ul>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg px-4 py-3 font-semibold flex items-center justify-between hover:from-purple-600 hover:to-pink-600 transition-colors"
      >
        <span>🏠 House Rules (Just Like Paper!)</span>
        <span className="text-2xl">{isOpen ? '−' : '+'}</span>
      </button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-4 bg-white rounded-lg border border-gray-200 p-6"
        >
          <p className="text-sm text-gray-600 mb-6">
            Set your family's rules - just like when you play with paper! No more arguments about what counts.
          </p>

          {/* Basic Rules */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">What Counts as Valid?</h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.allowProperNouns}
                  onChange={(e) => onChange({ ...settings, allowProperNouns: e.target.checked })}
                  className="mr-3 rounded text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Allow Proper Nouns</span>
                  <p className="text-sm text-gray-500">Names like "John" or places like "Paris"</p>
                </div>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.allowAlternateSpellings}
                  onChange={(e) => onChange({ ...settings, allowAlternateSpellings: e.target.checked })}
                  className="mr-3 rounded text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Accept Alternate Spellings</span>
                  <p className="text-sm text-gray-500">Like "grey" vs "gray" or "donut" vs "doughnut"</p>
                </div>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.allowMultiWordAnswers}
                  onChange={(e) => onChange({ ...settings, allowMultiWordAnswers: e.target.checked })}
                  className="mr-3 rounded text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Allow Multi-Word Answers</span>
                  <p className="text-sm text-gray-500">"New York", "ice cream", "teddy bear"</p>
                </div>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.allowForeignWords}
                  onChange={(e) => onChange({ ...settings, allowForeignWords: e.target.checked })}
                  className="mr-3 rounded text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Accept Foreign Language Words</span>
                  <p className="text-sm text-gray-500">Spanish, French, or other language words</p>
                </div>
              </label>
            </div>
          </div>

          {/* Word Length */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Minimum Word Length</h3>
            <div className="flex items-center space-x-4">
              <input
                type="range"
                min="1"
                max="5"
                value={settings.minimumWordLength}
                onChange={(e) => onChange({ ...settings, minimumWordLength: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-lg font-semibold text-gray-800 w-12 text-center">
                {settings.minimumWordLength}
              </span>
              <span className="text-sm text-gray-500">letters</span>
            </div>
          </div>

          {/* Scoring Mode */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Scoring Style</h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="scoring"
                  checked={settings.scoringMode === 'simple'}
                  onChange={() => onChange({ ...settings, scoringMode: 'simple', uniqueAnswerBonus: false })}
                  className="mr-3 text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Paper Style (Simple)</span>
                  <p className="text-sm text-gray-500">10 points per valid answer - just like paper!</p>
                </div>
              </label>

              <label className="flex items-center">
                <input
                  type="radio"
                  name="scoring"
                  checked={settings.scoringMode === 'enhanced'}
                  onChange={() => onChange({ ...settings, scoringMode: 'enhanced' })}
                  className="mr-3 text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Enhanced Digital</span>
                  <p className="text-sm text-gray-500">Extra points for longer words and creativity</p>
                </div>
              </label>
            </div>

            {settings.scoringMode === 'enhanced' && (
              <label className="flex items-center mt-3 ml-6">
                <input
                  type="checkbox"
                  checked={settings.uniqueAnswerBonus}
                  onChange={(e) => onChange({ ...settings, uniqueAnswerBonus: e.target.checked })}
                  className="mr-3 rounded text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Unique Answer Bonus</span>
                  <p className="text-sm text-gray-500">+5 points if nobody else wrote it</p>
                </div>
              </label>
            )}
          </div>

          {/* Special Modes */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Special Settings</h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.kidFriendlyMode}
                  onChange={(e) => onChange({ ...settings, kidFriendlyMode: e.target.checked })}
                  className="mr-3 rounded text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Kid-Friendly Mode</span>
                  <p className="text-sm text-gray-500">Extra time, helpful hints, and encouragement</p>
                </div>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={!settings.strictValidation}
                  onChange={(e) => onChange({ ...settings, strictValidation: !e.target.checked })}
                  className="mr-3 rounded text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Relaxed Validation</span>
                  <p className="text-sm text-gray-500">Be generous with close spellings and typos</p>
                </div>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.hostCanOverride}
                  onChange={(e) => onChange({ ...settings, hostCanOverride: e.target.checked })}
                  className="mr-3 rounded text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium">Host Can Override</span>
                  <p className="text-sm text-gray-500">Just like paper - host has final say on disputes</p>
                </div>
              </label>
            </div>
          </div>

          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onChange(defaultHouseRules)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
            >
              Default Rules
            </button>
            <button
              onClick={() => onChange({
                ...settings,
                kidFriendlyMode: true,
                minimumWordLength: 1,
                strictValidation: false,
                allowAlternateSpellings: true,
                scoringMode: 'simple'
              })}
              className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg text-sm font-medium transition-colors"
            >
              Kid-Friendly
            </button>
            <button
              onClick={() => onChange({
                ...settings,
                strictValidation: true,
                allowForeignWords: false,
                minimumWordLength: 3,
                scoringMode: 'enhanced',
                uniqueAnswerBonus: true
              })}
              className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-sm font-medium transition-colors"
            >
              Competitive
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default HouseRules;